import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Check, Sparkles, ArrowRight, Phone, Mail, Loader2, Gift } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { PLANS_DATA, PLANS_ORDER, getPrice, getPriceLabel } from "../data/plans";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ════════════════════════════════════════════════════════
   Billing Period Toggle
   ════════════════════════════════════════════════════════ */
function BillingToggle({ billingPeriod, onChange }) {
  return (
    <div className="flex justify-center mb-8">
      <div className="inline-flex bg-slate-800 rounded-lg p-1">
        <button
          onClick={() => onChange("monthly")}
          className={`px-5 py-2.5 rounded-md text-sm font-medium transition-colors ${
            billingPeriod === "monthly"
              ? "bg-teal-600 text-white"
              : "text-slate-400 hover:text-white"
          }`}
        >
          Mensual
        </button>
        <button
          onClick={() => onChange("annual")}
          className={`px-5 py-2.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
            billingPeriod === "annual"
              ? "bg-teal-600 text-white"
              : "text-slate-400 hover:text-white"
          }`}
        >
          Anual
          <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full font-semibold">
            Ahorra 18%
          </span>
        </button>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   Plan Card
   ════════════════════════════════════════════════════════ */
function PlanCard({ plan, billingPeriod, onSelectFree, onSelectPaid, onContactSales, processing }) {
  const price = getPrice(plan.id, billingPeriod);
  const priceLabel = getPriceLabel(billingPeriod);
  const isEnterprise = plan.isEnterprise;
  const isFree = plan.id === "free";
  // hasTrial is now informational only: it flags which plan corresponds to the
  // auto-assigned 14-day trial users get at registration. No per-card CTA;
  // the trial is granted server-side before this page is rendered.
  const showTrialBadge = plan.hasTrial;

  return (
    <div
      className={`relative rounded-2xl p-6 flex flex-col transition-all duration-200 ${
        plan.highlight
          ? "bg-gradient-to-b from-teal-500/10 to-teal-500/5 border-2 border-teal-500/50 shadow-lg shadow-teal-500/10"
          : "bg-slate-900/50 border border-slate-700 hover:border-slate-600"
      }`}
    >
      {plan.highlight && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="px-3 py-1 bg-teal-500 text-white text-xs font-semibold rounded-full flex items-center gap-1">
            <Sparkles size={12} />
            Recomendado
          </span>
        </div>
      )}

      <div className="mb-4">
        <h3 className="text-xl font-bold text-white">{plan.name}</h3>
        <p className="text-slate-400 text-sm">{plan.description}</p>
      </div>

      <div className="mb-5">
        {isEnterprise ? (
          <span className="text-3xl font-bold text-white">Custom</span>
        ) : isFree ? (
          <>
            <span className="text-4xl font-bold text-white">$0</span>
            <span className="text-slate-400 text-sm ml-1">para siempre</span>
          </>
        ) : (
          <>
            <span className="text-4xl font-bold text-white">${price}</span>
            <span className="text-slate-400 text-sm ml-1">{priceLabel}</span>
            {plan.minSeats > 1 && (
              <p className="text-xs text-slate-500 mt-1">Mínimo {plan.minSeats} usuarios</p>
            )}
          </>
        )}
      </div>

      {/* Trial badge — only on the plan that backs the auto-assigned 14-day trial */}
      {showTrialBadge && (
        <div
          data-testid={`trial-badge-${plan.id}`}
          className="mb-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-teal-500/10 border border-teal-500/30 text-[11px] font-medium text-teal-300 self-start"
        >
          <Gift size={12} />
          14 días gratis al registrarte · sin tarjeta
        </div>
      )}

      {/* Feature subtitle */}
      {!isFree && (
        <p className="text-xs text-teal-400 font-medium mb-2">{plan.subtitle}</p>
      )}

      <ul className="space-y-2.5 mb-6 flex-1">
        {plan.features.map((feature, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <Check className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
            <span className="text-slate-300">{feature}</span>
          </li>
        ))}
      </ul>

      {/* CTAs */}
      <div className="space-y-2 mt-auto">
        {isFree && (
          <Button
            onClick={onSelectFree}
            disabled={processing}
            className="w-full h-11 bg-teal-600 hover:bg-teal-500 text-white"
          >
            {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Comenzar gratis"}
          </Button>
        )}

        {!isFree && !isEnterprise && (
          <Button
            onClick={() => onSelectPaid(plan.id)}
            disabled={processing}
            className="w-full h-11 bg-teal-600 hover:bg-teal-500 text-white"
          >
            {processing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <span className="flex items-center gap-2">
                Comprar ahora
                <ArrowRight size={16} />
              </span>
            )}
          </Button>
        )}

        {isEnterprise && (
          <Button
            onClick={onContactSales}
            variant="outline"
            className="w-full h-11 border-slate-600 text-slate-300 hover:bg-slate-800"
          >
            <Phone size={16} className="mr-2" />
            Contactar ventas
          </Button>
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   Contact Sales Modal
   ════════════════════════════════════════════════════════ */
function ContactSalesModal({ onClose }) {
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-md w-full p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
          <Phone className="w-8 h-8 text-emerald-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-3">Contacta a nuestro equipo</h2>
        <p className="text-slate-400 mb-6">
          Nuestro equipo de ventas creará una solución personalizada para tu organización.
        </p>
        <div className="bg-slate-800/50 rounded-xl p-4 mb-6 space-y-3">
          <a href="mailto:ventas@notalia.mx" className="flex items-center justify-center gap-2 text-teal-400 hover:text-teal-300">
            <Mail size={18} /> ventas@notalia.mx
          </a>
          <a href="tel:+525512345678" className="flex items-center justify-center gap-2 text-teal-400 hover:text-teal-300">
            <Phone size={18} /> +52 55 1234 5678
          </a>
        </div>
        <Button onClick={onClose} className="w-full bg-teal-600 hover:bg-teal-500 text-white h-11">
          Entendido
        </Button>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   Main Page
   ════════════════════════════════════════════════════════ */
export default function RegisterPlan() {
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [showContactSales, setShowContactSales] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { checkAuth: refreshAuth } = useAuth();

  useEffect(() => {
    const init = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (!res.data.organization_id) {
          navigate("/register/organization", { replace: true });
          return;
        }
        setCheckingAuth(false);
      } catch {
        navigate("/register", { replace: true });
      }
    };
    init();
  }, [navigate]);

  /* ── Free plan ── */
  const handleSelectFree = async () => {
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/register/select-plan`, { plan: "free" }, { withCredentials: true });
      await refreshAuth();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Error al seleccionar plan");
      setLoading(false);
    }
  };

  /* ── Paid plan → Stripe Checkout ── */
  const handleSelectPaid = async (planId) => {
    setLoading(true);
    setError("");
    try {
      const plan = PLANS_DATA[planId];
      // First mark registration complete
      await axios.post(`${API}/auth/register/select-plan`, { plan: planId }, { withCredentials: true });
      
      // Then create checkout session
      const response = await axios.post(
        `${API}/billing/checkout`,
        {
          plan: planId,
          billing_period: billingPeriod,
          quantity: plan.minSeats,
          coupon_code: null,
          origin_url: window.location.origin,
        },
        { withCredentials: true }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      console.error("Checkout error:", err);
      setError(err.response?.data?.detail || "Error al iniciar el pago. Intenta de nuevo.");
      setLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500" />
      </div>
    );
  }

  const plansToShow = PLANS_ORDER.map((id) => PLANS_DATA[id]);

  return (
    <div className="min-h-screen bg-slate-950 py-8 px-4" data-testid="register-plan-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <img src="/notalia-logo.png" alt="Notalia" className="h-10 mx-auto brightness-0 invert mb-6" />

          {/* Progress indicator */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {["Cuenta", "Organización", "Plan"].map((label, i) => (
              <div key={label} className="flex items-center gap-2">
                {i > 0 && <div className={`w-8 h-px ${i <= 2 ? "bg-teal-500" : "bg-slate-700"}`} />}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  i < 2
                    ? "bg-teal-500/20 border-2 border-teal-500 text-teal-400"
                    : "bg-teal-500 text-white"
                }`}>
                  {i < 2 ? "✓" : i + 1}
                </div>
                <span className="text-teal-400 text-sm font-medium hidden sm:inline">{label}</span>
              </div>
            ))}
          </div>

          <h1 className="text-3xl font-bold text-white mb-3">Elige tu plan</h1>
          <p className="text-slate-400 text-lg max-w-xl mx-auto">
            Tu cuenta ya tiene <strong className="text-teal-400">Pro completo</strong> activo por 14 días sin tarjeta.
            Elige a qué plan quieres pasar cuando termine el trial.
          </p>
        </div>

        {/* Billing Toggle */}
        <BillingToggle billingPeriod={billingPeriod} onChange={setBillingPeriod} />

        {/* Error */}
        {error && (
          <div className="max-w-2xl mx-auto mb-6 bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm text-center">
            {error}
          </div>
        )}

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-10">
          {plansToShow.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              billingPeriod={billingPeriod}
              onSelectFree={handleSelectFree}
              onSelectPaid={handleSelectPaid}
              onContactSales={() => setShowContactSales(true)}
              processing={loading}
            />
          ))}
        </div>

        {/* Bottom note */}
        <div className="text-center">
          <p className="text-slate-500 text-sm">
            Los primeros 14 días son Pro completo sin tarjeta. Después pasarás al plan que elijas. Cancela cuando quieras.
          </p>
        </div>

        {/* Comparison Table */}
        <div className="mt-16 bg-slate-900/50 rounded-2xl border border-slate-800 p-6 overflow-x-auto">
          <h2 className="text-xl font-bold text-white mb-6 text-center">Comparativa de planes</h2>
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left py-3 px-4 text-slate-400 font-medium">Característica</th>
                {plansToShow.map((p) => (
                  <th key={p.id} className={`text-center py-3 px-4 font-medium ${p.highlight ? "text-teal-400" : "text-white"}`}>
                    {p.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {[
                { label: "Precio (mensual)", values: ["$0", "$19/usr", "$39/usr", "$59/usr", "Custom"] },
                { label: "Usuarios mín.", values: ["1", "1", "3", "5", "Custom"] },
                { label: "Clientes", values: ["10", "50", "250", "500", "Ilimitado"] },
                { label: "Almacenamiento", values: ["200 MB", "2 GB", "10 GB", "50 GB", "500 GB+"] },
                { label: "Onboardings/mes", values: ["3", "15", "50", "150", "Ilimitado"] },
                { label: "Screenings/mes", values: ["5", "30", "100", "300", "Ilimitado"] },
                { label: "PDFs/mes", values: ["3", "15", "50", "150", "Ilimitado"] },
                { label: "OCR/mes", values: ["—", "—", "—", "300", "Ilimitado"] },
              ].map((row, i) => (
                <tr key={i}>
                  <td className="py-3 px-4 text-slate-300">{row.label}</td>
                  {row.values.map((val, j) => (
                    <td key={j} className="text-center py-3 px-4 text-slate-400">{val}</td>
                  ))}
                </tr>
              ))}
              {[
                { label: "Reportes XML SAT/UIF", plans: ["standard", "pro", "enterprise"] },
                { label: "Dashboard cumplimiento", plans: ["standard", "pro", "enterprise"] },
                { label: "OCR automático", plans: ["pro", "enterprise"] },
                { label: "Bitácora / audit log", plans: ["pro", "enterprise"] },
                { label: "API de integración", plans: ["pro", "enterprise"] },
                { label: "White-label", plans: ["enterprise"] },
                { label: "Dominio propio", plans: ["enterprise"] },
                { label: "SLA garantizado", plans: ["enterprise"] },
              ].map((row, i) => (
                <tr key={`feat-${i}`}>
                  <td className="py-3 px-4 text-slate-300">{row.label}</td>
                  {PLANS_ORDER.map((planId) => (
                    <td key={planId} className="text-center py-3 px-4">
                      {row.plans.includes(planId) ? (
                        <Check className="w-5 h-5 text-teal-400 mx-auto" />
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {showContactSales && <ContactSalesModal onClose={() => setShowContactSales(false)} />}
    </div>
  );
}
